import React from 'react';
import { FileX } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';
import { formatDate } from '../../utils/dateFormat';

const ReportViewer = ({ type, data, loading }) => {
  const { organization } = useOrganization();
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="h-4 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || (Array.isArray(data) && data.length === 0)) {
    return (
      <div className="p-8 text-center">
        <FileX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No data available for this report</p>
      </div>
    );
  }

  const renderTable = (columns, rows, showTotals = false) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col, index) => (
              <th
                key={index}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {safeRows.map((row, index) => (
            <tr key={index} className="hover:bg-gray-50">
              {Object.values(row).map((value, cellIndex) => (
                <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {typeof value === 'number' ? value.toLocaleString() : (value || '-')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {showTotals && (
          <tfoot className="bg-gray-50">
            <tr>
              <td className="px-6 py-3 text-sm font-medium text-gray-900">Total</td>
              {columns.slice(1).map((_, index) => (
                <td key={index} className="px-6 py-3 text-sm font-medium text-gray-900">
                  {/* Calculate totals for numeric columns */}
                  {typeof safeRows[0]?.[Object.keys(safeRows[0] || {})[index + 1]] === 'number'
                    ? safeRows.reduce((sum, row) => sum + (Object.values(row)[index + 1] || 0), 0).toLocaleString()
                    : '-'
                  }
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
  };

  switch (type) {
    case 'trial-balance':
      return renderTable(
        ['Account', 'Opening Debit', 'Opening Credit', 'Period Debit', 'Period Credit', 'Closing Debit', 'Closing Credit'],
        data,
        true
      );

    case 'balance-sheet':
      return (
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Assets</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2">Current Assets</h4>
                {(data.assets?.current || []).map((asset, index) => (
                  <div key={index} className="flex justify-between py-1">
                    <span>{asset.name}</span>
                    <span>{formatCurrency(asset.amount || 0, organization?.currency_code)}</span>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="font-medium mb-2">Long-term Assets</h4>
                {(data.assets?.longTerm || []).map((asset, index) => (
                  <div key={index} className="flex justify-between py-1">
                    <span>{asset.name}</span>
                    <span>{formatCurrency(asset.amount || 0, organization?.currency_code)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-3">Liabilities</h3>
            {(data.liabilities || []).map((liability, index) => (
              <div key={index} className="flex justify-between py-1">
                <span>{liability.name}</span>
                <span>{formatCurrency(liability.amount || 0, organization?.currency_code)}</span>
              </div>
            ))}
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-3">Equity</h3>
            {(data.equity || []).map((equity, index) => (
              <div key={index} className="flex justify-between py-1">
                <span>{equity.name}</span>
                <span>{formatCurrency(equity.amount || 0, organization?.currency_code)}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case 'profit-loss':
      return (
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Revenue</h3>
            {(data.revenue || []).map((item, index) => (
              <div key={index} className="flex justify-between py-1">
                <span>{item.name}</span>
                <span>{formatCurrency(item.amount || 0, organization?.currency_code)}</span>
              </div>
            ))}
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-3">Expenses</h3>
            {(data.expenses || []).map((item, index) => (
              <div key={index} className="flex justify-between py-1">
                <span>{item.name}</span>
                <span>{formatCurrency(item.amount || 0, organization?.currency_code)}</span>
              </div>
            ))}
          </div>
          
          <div className="border-t pt-4">
            <div className="flex justify-between font-semibold text-lg">
              <span>Net Profit</span>
              <span className={data.netProfit < 0 ? 'text-red-600' : 'text-green-600'}>
                {formatCurrency(data.netProfit || 0, organization?.currency_code)}
              </span>
            </div>
          </div>
        </div>
      );

    case 'journal':
      const journalData = Array.isArray(data) ? data : [];
      const mappedJournalData = journalData.map(entry => ({
        date: formatDate(entry.date),
        transId: entry.transId,
        accountName: entry.accountName,
        description: entry.description,
        debit: entry.debit || 0,
        credit: entry.credit || 0
      }));
      
      // Calculate totals
      const totalDebits = mappedJournalData.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0);
      const totalCredits = mappedJournalData.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0);
      const transactionCount = mappedJournalData.length;
      
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Trans ID', 'Account Name', 'Description', 'Debit', 'Credit'].map((col, index) => (
                  <th
                    key={index}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mappedJournalData.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.transId}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.accountName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {row.debit > 0 ? row.debit.toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {row.credit > 0 ? row.credit.toLocaleString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">Total</td>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">{transactionCount}</td>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">-</td>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">-</td>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">{totalDebits.toLocaleString()}</td>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">{totalCredits.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      );

    case 'vat':
      const vatData = Array.isArray(data) ? data : [];
      const totalAmount = vatData.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
      const totalVatInput = vatData.reduce((sum, row) => sum + (parseFloat(row.vatInput) || 0), 0);
      const totalVatOutput = vatData.reduce((sum, row) => sum + (parseFloat(row.vatOutput) || 0), 0);
      const vatTransactionCount = vatData.length;
      const netVat = totalVatOutput - totalVatInput;
      
      return (
        <div className="space-y-6">
          {/* VAT Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-600 mb-1">Total Transactions</h4>
              <p className="text-2xl font-bold text-blue-900">{vatTransactionCount}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-600 mb-1">VAT Input</h4>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(totalVatInput, organization?.currency_code)}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-orange-600 mb-1">VAT Output</h4>
              <p className="text-2xl font-bold text-orange-900">{formatCurrency(totalVatOutput, organization?.currency_code)}</p>
            </div>
            <div className={`${netVat >= 0 ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'} border rounded-lg p-4`}>
              <h4 className={`text-sm font-medium mb-1 ${netVat >= 0 ? 'text-red-600' : 'text-purple-600'}`}>Net VAT {netVat >= 0 ? 'Payable' : 'Refundable'}</h4>
              <p className={`text-2xl font-bold ${netVat >= 0 ? 'text-red-900' : 'text-purple-900'}`}>{formatCurrency(Math.abs(netVat), organization?.currency_code)}</p>
            </div>
          </div>
          
          {/* VAT Transactions Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h3 className="text-lg font-semibold text-gray-900">VAT Transaction Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    {['Order No', 'Trans ID', 'Date', 'Amount', 'VAT %', 'VAT Input', 'VAT Output', 'Running Balance'].map((col, index) => (
                      <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {vatData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.orderNo || '-'}</td>
                      <td className="px-4 py-3 text-sm text-blue-600 font-mono">{row.transId || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(row.completionDate) || '-'}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatCurrency(row.amount || 0, organization?.currency_code)}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {row.vatPercent || 0}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-700">
                        {row.vatInput > 0 ? formatCurrency(row.vatInput, organization?.currency_code) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-orange-700">
                        {row.vatOutput > 0 ? formatCurrency(row.vatOutput, organization?.currency_code) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">{formatCurrency(row.runningBalance || 0, organization?.currency_code)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-800 text-white">
                  <tr>
                    <td className="px-4 py-4 text-sm font-bold">TOTAL</td>
                    <td className="px-4 py-4 text-sm font-bold">{vatTransactionCount}</td>
                    <td className="px-4 py-4 text-sm">-</td>
                    <td className="px-4 py-4 text-sm font-bold">{formatCurrency(totalAmount, organization?.currency_code)}</td>
                    <td className="px-4 py-4 text-sm">-</td>
                    <td className="px-4 py-4 text-sm font-bold text-green-300">{formatCurrency(totalVatInput, organization?.currency_code)}</td>
                    <td className="px-4 py-4 text-sm font-bold text-orange-300">{formatCurrency(totalVatOutput, organization?.currency_code)}</td>
                    <td className="px-4 py-4 text-sm font-bold">{formatCurrency(netVat, organization?.currency_code)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      );

    case 'cash-accounts':
      return renderTable(
        ['Account Name', 'Account Number', 'Balance', 'Transactions'],
        Array.isArray(data) ? data : []
      );

    case 'customer-receivable':
      return renderTable(
        ['Name', 'Contact', 'Reg No', 'Order No', 'TR No', 'Claim No', 'Completion Date', 'Amount', 'Received', 'Balance', 'Past Due Date'],
        data
      );

    case 'sales-orders':
      return renderTable(
        ['Order No', 'Invoice #', 'Customer', 'Reg #', 'App Date', 'Invoiced On', 'Branch', 'Vendor', 'Technician', 'Service Name', 'Quantity', 'Rate', 'Subtotal', 'VAT', 'VAT Total', 'Discount', 'Total Amount', 'Collected', 'Balance', 'Status'],
        data
      );

    case 'job-margin':
      const jobData = Array.isArray(data) ? data : [];
      return renderTable(
        ['Job Card No', 'Customer', 'Revenue', 'Cost', 'Margin', 'Margin %'],
        jobData.map(job => ({
          ...job,
          marginPercent: `${job.marginPercent}%`
        }))
      );

    case 'vendor-payable':
      return renderTable(
        ['Vendor', 'Invoice No', 'Part/Service', 'Due Date', 'Amount', 'Paid', 'Remaining'],
        data
      );

    case 'purchase':
      return renderTable(
        ['PO No', 'Invoice Date', 'Issue Date', 'Delivery Date', 'Credit Terms', 'Vendor Name', 'Invoice #', 'Part Name', 'Part #', 'Service Name', 'Quantity', 'Cost', 'VAT', 'Total'],
        data
      );

    case 'inventory':
      return renderTable(
        ['Part No', 'Part Name', 'Brand', 'Sub Group', 'Opening Qty', 'Stock In', 'Stock Out', 'Available Qty', 'PO #'],
        data
      );

    case 'sales-return':
      return renderTable(
        ['Return No', 'Return Date', 'Job Card No', 'Customer', 'Reason', 'Status', 'Total Items', 'Total Quantity', 'Total Amount', 'Refund Amount', 'Processed By', 'Approved By'],
        Array.isArray(data) ? data.map(item => ({
          returnNo: item.returnNo,
          returnDate: formatDate(item.returnDate),
          jobCardNo: item.jobCardNo,
          customer: item.customer,
          reason: item.reason,
          status: item.status,
          totalItems: item.totalItems,
          totalQuantity: item.totalQuantity,
          totalAmount: formatCurrency(item.totalAmount || 0, organization?.currency_code),
          refundAmount: formatCurrency(item.refundAmount || 0, organization?.currency_code),
          processedBy: item.processedBy,
          approvedBy: item.approvedBy
        })) : []
      );

    case 'order-report':
      if (!data?.report) return <div className="p-8 text-center"><p className="text-gray-500">No order data available</p></div>;
      
      return (
        <div className="p-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-600">Total Orders</h4>
              <p className="text-2xl font-bold text-blue-900">{data.report.summary.totalOrders}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-green-600">Completed</h4>
              <p className="text-2xl font-bold text-green-900">{data.report.summary.completedOrders}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-600">Pending</h4>
              <p className="text-2xl font-bold text-yellow-900">{data.report.summary.pendingOrders}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-red-600">Cancelled</h4>
              <p className="text-2xl font-bold text-red-900">{data.report.summary.cancelledOrders}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-purple-600">Total Value</h4>
              <p className="text-2xl font-bold text-purple-900">{formatCurrency(data.report.summary.totalValue || 0, organization?.currency_code)}</p>
            </div>
          </div>
          
          {/* Orders Table */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Order Details</h3>
            {renderTable(
              ['Order #', 'Customer', 'Vehicle', 'Date', 'Status', 'Amount', 'Branch'],
              (data.report.orders || []).map(order => ({
                orderNumber: order.orderNumber,
                customerName: order.customerName,
                vehicleInfo: order.vehicleInfo,
                orderDate: order.orderDate,
                status: order.status,
                totalAmount: formatCurrency(order.totalAmount || 0, organization?.currency_code),
                branch: order.branch
              }))
            )}
          </div>
        </div>
      );

    case 'all-orders':
      if (!data?.report) return <div className="p-8 text-center"><p className="text-gray-500">No orders data available</p></div>;
      
      return (
        <div className="p-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-600">Total Orders</h4>
              <p className="text-2xl font-bold text-blue-900">{data.report.summary.totalOrders}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-green-600">Total Value</h4>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(data.report.summary.totalValue || 0, organization?.currency_code)}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-purple-600">Avg Order Value</h4>
              <p className="text-2xl font-bold text-purple-900">{formatCurrency(data.report.summary.averageOrderValue || 0, organization?.currency_code)}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-orange-600">Top Customer</h4>
              <p className="text-lg font-bold text-orange-900">{data.report.summary.topCustomer}</p>
            </div>
          </div>
          
          {/* Monthly Breakdown */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Monthly Breakdown</h3>
            {renderTable(
              ['Month', 'Orders', 'Value'],
              (data.report.monthlyBreakdown || []).map(month => ({
                month: month.month,
                orders: month.orders,
                value: formatCurrency(month.value || 0, organization?.currency_code)
              }))
            )}
          </div>
          
          {/* Status Breakdown */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Status Breakdown</h3>
            {renderTable(
              ['Status', 'Count', 'Percentage'],
              (data.report.statusBreakdown || []).map(status => ({
                status: status.status,
                count: status.count,
                percentage: `${status.percentage}%`
              }))
            )}
          </div>
          
          {/* Top Services */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Top Services</h3>
            {renderTable(
              ['Service', 'Count', 'Revenue'],
              (data.report.topServices || []).map(service => ({
                service: service.service,
                count: service.count,
                revenue: formatCurrency(service.revenue || 0, organization?.currency_code)
              }))
            )}
          </div>
        </div>
      );

    default:
      return (
        <div className="p-8 text-center">
          <p className="text-gray-500">Report type not supported</p>
        </div>
      );
  }
};

export default ReportViewer;